import { useResponsiveValue } from '@lemonade-hq/cdk';
import React from 'react';
import styled, { keyframes } from 'styled-components';

const upAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1);
    opacity: 0.7;
    top: calc(var(--offset) * -1);
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
    top: calc(var(--offset) * -1);

  }
`;

const downAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1);
    opacity: 0.7;
    bottom: calc(var(--offset) * -1);
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
    bottom: calc(var(--offset) * -1);

  }
`;

const rightAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1);
    opacity: 0.7;
    right: calc(var(--offset) * -1);
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
    right: calc(var(--offset) * -1);

  }
`;

const leftAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1);
    opacity: 0.7;
    left: calc(var(--offset) * -1);
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
    left: calc(var(--offset) * -1);
  }
`;

const animationMap = {
  up: upAnimation,
  down: downAnimation,
  left: leftAnimation,
  right: rightAnimation,
};

interface ParticleProps {
  size: number;
  animationName: 'up' | 'down' | 'left' | 'right';
  animationDuration: number;
  animationDelay: number;
  offset: number;
  color: string;
  position: Record<string, string>;
}

const Particle = styled.div<ParticleProps>`
  position: absolute;
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  background: ${props => props.color};
  border-radius: 50%;
  z-index: 0;
  animation: ${props => animationMap[props.animationName]} ${props => `${props.animationDuration}s`}
    cubic-bezier(0, 0.98, 0.83, 0.91) infinite;
  animation-delay: ${props => `${props.animationDelay}s`};
  --offset: ${props => `${props.offset}px`};
  --final-offset: ${props => `${props.offset + 5}px`};
  ${props =>
    Object.entries(props.position)
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n')}
`;

interface ParticlesProps {
  qtt: [number, number, number];
  position: 'start' | 'end';
  orientation: 'vertical' | 'horizontal';
  colors?: string[];
}

const DEFAULT_COLORS = ['#FFB4DA', '#ff0083'];

const getParticleConfig = (colors?: string[]) => {
  const size = Math.random() * 2 + 2;
  const colorOptions = colors && colors.length >= 2 ? colors : DEFAULT_COLORS;
  const colorIndex = Math.floor(Math.random() * colorOptions.length);
  const color = colorOptions[colorIndex];
  const randomDelay = Math.random() * 2;
  const speed = Math.random() + 1.5;
  const offset = Math.random() * 2 + 9;

  return { size, color, randomDelay, speed, offset };
};

type DirectionConfig = {
  mainAxis: 'left' | 'top';
  crossAxis: 'top' | 'left';
  startProp: 'left' | 'top';
  endProp: 'bottom' | 'right';
  animationStart: 'left' | 'up';
  animationEnd: 'right' | 'down';
};

const directionConfigs: Record<'horizontal' | 'vertical', DirectionConfig> = {
  vertical: {
    mainAxis: 'left',
    crossAxis: 'top',
    startProp: 'top',
    endProp: 'bottom',
    animationStart: 'up',
    animationEnd: 'down',
  },
  horizontal: {
    mainAxis: 'top',
    crossAxis: 'left',
    startProp: 'left',
    endProp: 'right',
    animationStart: 'left',
    animationEnd: 'right',
  },
};

export const Particles: React.FC<ParticlesProps> = ({ qtt: qttList, position, orientation, colors }) => {
  const qtt = useResponsiveValue(qttList);
  const config = directionConfigs[orientation];
  const validColors = colors && colors.length >= 2 ? colors : DEFAULT_COLORS;

  return (
    <>
      {Array.from({ length: qtt }).map((_, index) => {
        const { size, color, randomDelay, speed, offset } = getParticleConfig(validColors);
        const animationName = position === 'end' ? config.animationEnd : config.animationStart;

        const positionStyles: Record<string, string> = {
          [config.mainAxis]: `${index * (100 / qtt)}%`,
          [config.startProp]: position === 'start' ? '10px' : 'auto',
          [config.endProp]: position === 'end' ? '10px' : 'auto',
        };

        if (orientation === 'horizontal') {
          positionStyles.bottom = '0';
        }

        return (
          <Particle
            key={index}
            size={size}
            color={color}
            animationName={animationName}
            animationDuration={speed}
            animationDelay={randomDelay}
            offset={offset}
            position={positionStyles}
          />
        );
      })}
    </>
  );
};
